import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { NotificationBell } from '@/components/NotificationBell';
import { useAuth } from '@/hooks/useAuth';
import { useBilling } from '@/hooks/useBilling';
import { useCreateListing } from '@/contexts/CreateListingContext';
import { features as featureCatalog } from '@/lib/featuresData';
import solutionAgentImg from '@/assets/solution-agent.jpg';
import solutionOwnerImg from '@/assets/solution-owner.jpg';
import {
  Home,
  User,
  LogIn,
  LogOut,
  Menu,
  X,
  LayoutList,
  Sparkles,
  CreditCard,
  BookOpen,
  Mail,
  BarChart3,
  Layers,
  ChevronDown,
  Eraser,
  Eye,
  ShieldOff,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const navLinks = [
  { href: '/', label: 'Home', icon: Home },
  { href: '/pricing', label: 'Pricing', icon: CreditCard },
];

const solutionsLinks = [
  {
    href: '/real-estate-marketing-software',
    label: 'For Real Estate Agents',
    description: 'Win more listings and close sales faster with AI-powered marketing.',
    icon: Sparkles,
    image: solutionAgentImg,
  },
  {
    href: '/single-property-kit',
    label: 'For Homeowners',
    description: 'Sell your own property with a complete AI listing kit.',
    icon: Home,
    image: solutionOwnerImg,
  },
];


const resourcesLinks = [
  { href: '/blog', label: 'Blog', description: 'Tips, guides and product updates.', icon: BookOpen },
  { href: '/contact', label: 'Contact', description: 'Get in touch with our team.', icon: Mail },
];

// Standalone Photo AI tools with dedicated landing pages (not in /features/:id)
const extraPhotoTools = [
  { href: '/ai-declutter-photos', title: 'AI Declutter', icon: Eraser },
  { href: '/remove-photographer-reflections', title: 'Remove Reflections', icon: Eye },
  { href: '/blur-family-photos', title: 'Blur Family Photos', icon: ShieldOff },
];

export function Header() {
  const { user, signOut } = useAuth();
  const { isSubscriber } = useBilling();
  const { triggerCreate } = useCreateListing();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;
  const isFeaturesActive = location.pathname === '/features' || location.pathname.startsWith('/features/');
  const isSolutionsActive = solutionsLinks.some((l) => location.pathname === l.href);
  const isResourcesActive = resourcesLinks.some((l) => location.pathname === l.href);

  const handleLogin = () => navigate('/auth');
  const handleProfile = () => navigate('/profile');
  const handleMyListings = () => navigate('/my-listings');
  const handleCreateListing = () => {
    // Trigger create via context (works for any page)
    // Navigate to homepage where the creation chat flow is
    triggerCreate();
    if (location.pathname !== '/') {
      navigate('/');
    }
  };
  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/30 bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 shadow-[0_1px_3px_0_hsl(var(--foreground)/0.03)]">
      <div className="container max-w-7xl mx-auto px-4">
        <div className="flex h-16 md:h-[72px] items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            onClick={() => {
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img src="/logo.png" alt="ListBright logo" className="h-16 md:h-20" width={147} height={80} style={{ background: 'transparent' }} />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Features dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-tour="nav-features"
                  className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[13px] font-medium tracking-wide transition-all duration-200 ${
                    isFeaturesActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  }`}
                >
                  The Suite
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[560px] p-4">
                {(() => {
                  const photoIds = ['virtual-staging', 'photo-enhancement', 'golden-hour', 'blue-sky', 'lawn-replacement', 'custom-prompt'];
                  const photoTools = photoIds
                    .map((id) => featureCatalog.find((f) => f.id === id))
                    .filter((f): f is typeof featureCatalog[number] => Boolean(f));
                  const marketingTools = featureCatalog.filter((f) => !photoIds.includes(f.id));
                  const renderItem = (f: typeof featureCatalog[number]) => {
                    const FIcon = f.icon;
                    return (
                      <button
                        key={f.id}
                        onClick={() => navigate(`/features/${f.id}`)}
                        className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-muted/60 transition-colors"
                      >
                        <FIcon className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-sm font-medium text-foreground truncate">{f.title}</span>
                      </button>
                    );
                  };
                  return (
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      <div>
                        <div className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          Photo AI
                        </div>
                        <div className="flex flex-col">
                          {photoTools.map(renderItem)}
                          {extraPhotoTools.map((t) => {
                            const TIcon = t.icon;
                            return (
                              <button
                                key={t.href}
                                onClick={() => navigate(t.href)}
                                className="w-full text-left flex items-center gap-2.5 px-2.5 py-2 rounded-md hover:bg-muted/60 transition-colors"
                              >
                                <TIcon className="w-4 h-4 text-primary shrink-0" />
                                <span className="text-sm font-medium text-foreground truncate">{t.title}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div>
                        <div className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                          Video &amp; Marketing
                        </div>
                        <div className="flex flex-col">{marketingTools.map(renderItem)}</div>
                      </div>
                    </div>
                  );
                })()}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Solutions dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[13px] font-medium tracking-wide transition-all duration-200 ${
                    isSolutionsActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  }`}
                >
                  Solutions
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[520px] p-3">
                <div className="grid grid-cols-2 gap-3">
                  {solutionsLinks.map((l) => (
                    <button
                      key={l.href}
                      onClick={() => navigate(l.href)}
                      className="group flex flex-col text-left rounded-xl p-2.5 border border-border/40 bg-card hover:border-primary/30 hover:bg-muted/40 transition-all"
                    >
                      <div className="aspect-[3/2] rounded-lg overflow-hidden bg-muted mb-2.5">
                        <img
                          src={l.image}
                          alt={l.label}
                          loading="lazy"
                          width={240}
                          height={160}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="px-0.5">
                        <div className="text-sm font-semibold text-foreground">{l.label}</div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                          {l.description}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Resources dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`inline-flex items-center gap-1 px-4 py-2 rounded-lg text-[13px] font-medium tracking-wide transition-all duration-200 ${
                    isResourcesActive
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                  }`}
                >
                  Resources
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72 p-2">
                {resourcesLinks.map((l) => {
                  const Icon = l.icon;
                  return (
                    <DropdownMenuItem
                      key={l.href}
                      onClick={() => navigate(l.href)}
                      className="rounded-md items-start py-2"
                    >
                      <Icon className="w-4 h-4 mr-2 mt-0.5 text-primary shrink-0" />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{l.label}</span>
                        <span className="text-xs text-muted-foreground line-clamp-1">
                          {l.description}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`px-4 py-2 rounded-lg text-[13px] font-medium tracking-wide transition-all duration-200 ${
                  isActive(link.href)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth & Actions */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="gap-2" data-tour="account-menu" aria-label="Account menu">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="hidden lg:inline">Account</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={handleProfile}>
                      <User className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleMyListings}>
                      <LayoutList className="w-4 h-4 mr-2" />
                      My Listings
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/usage')}>
                      <BarChart3 className="w-4 h-4 mr-2" />
                      Usage Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                      <LogOut className="w-4 h-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="default" size="sm" onClick={handleCreateListing} data-tour="create-listing">
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create Listing
                </Button>
              </>
            ) : (
              <>
                <Button variant="ghost" size="sm" onClick={handleLogin}>
                  <LogIn className="w-4 h-4 mr-2" />
                  Login
                </Button>
                <Button
                  variant="hero"
                  size="sm"
                  onClick={handleLogin}
                  className="h-10 px-5 rounded-full shadow-elevated hover:shadow-glow font-semibold"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get Started Free
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="flex md:hidden items-center gap-2">
            {user && <NotificationBell />}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]" aria-label="Open menu">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[85vw] max-w-[400px] p-0 flex flex-col">
                <div className="flex flex-col h-full min-h-0 flex-1">
                  <div className="flex items-center justify-between p-5 border-b border-border/40 shrink-0">
                    <img src="/logo.png" alt="ListBright logo" className="h-14" width={102} height={56} />
                  </div>

                  <div className="relative flex-1 min-h-0">
                    <nav className="flex flex-col gap-1 p-4 h-full overflow-y-auto overscroll-contain scroll-smooth">
                    <SheetClose asChild>
                      <Link
                        to="/"
                        onClick={() => {
                          if (location.pathname === '/') {
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                        className={`flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium transition-colors ${
                          isActive('/')
                            ? 'bg-primary/10 text-primary'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
                        }`}
                      >
                        <Home className="w-5 h-5" />
                        Home
                      </Link>
                    </SheetClose>

                    {/* Features accordion */}
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="features" className="border-0">
                        <AccordionTrigger
                          className={`flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium hover:no-underline ${
                            isFeaturesActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
                          }`}
                        >
                          <span className="flex items-center gap-3 flex-1">
                            <Layers className="w-5 h-5" />
                            The Suite
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-3 pb-1">
                          {featureCatalog.map((f) => {
                            const FIcon = f.icon;
                            return (
                              <SheetClose asChild key={f.id}>
                                <Link
                                  to={`/features/${f.id}`}
                                  className="flex items-start gap-3 px-4 py-3 min-h-[44px] rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                  <FIcon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                                  <span>{f.title}</span>
                                </Link>
                              </SheetClose>
                            );
                          })}
                          {extraPhotoTools.map((t) => {
                            const TIcon = t.icon;
                            return (
                              <SheetClose asChild key={t.href}>
                                <Link
                                  to={t.href}
                                  className="flex items-start gap-3 px-4 py-3 min-h-[44px] rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                  <TIcon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                                  <span>{t.title}</span>
                                </Link>
                              </SheetClose>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    {/* Solutions accordion */}
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="solutions" className="border-0">
                        <AccordionTrigger
                          className={`flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium hover:no-underline ${
                            isSolutionsActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
                          }`}
                        >
                          <span className="flex items-center gap-3 flex-1">
                            <Sparkles className="w-5 h-5" />
                            Solutions
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-3 pb-1">
                          {solutionsLinks.map((l) => {
                            const Icon = l.icon;
                            return (
                              <SheetClose asChild key={l.href}>
                                <Link
                                  to={l.href}
                                  className="flex items-start gap-3 px-4 py-3 min-h-[44px] rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                  <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                                  <div className="flex flex-col">
                                    <span className="font-medium text-foreground">{l.label}</span>
                                    <span className="text-xs">{l.description}</span>
                                  </div>
                                </Link>
                              </SheetClose>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    {/* Resources accordion */}
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="resources" className="border-0">
                        <AccordionTrigger
                          className={`flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium hover:no-underline ${
                            isResourcesActive
                              ? 'bg-primary/10 text-primary'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
                          }`}
                        >
                          <span className="flex items-center gap-3 flex-1">
                            <BookOpen className="w-5 h-5" />
                            Resources
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pl-3 pb-1">
                          {resourcesLinks.map((l) => {
                            const Icon = l.icon;
                            return (
                              <SheetClose asChild key={l.href}>
                                <Link
                                  to={l.href}
                                  className="flex items-center gap-3 px-4 py-3 min-h-[44px] rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted"
                                >
                                  <Icon className="w-4 h-4 text-primary shrink-0" />
                                  <span>{l.label}</span>
                                </Link>
                              </SheetClose>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    {navLinks.slice(1).map((link) => {
                      const Icon = link.icon;
                      return (
                        <SheetClose asChild key={link.href}>
                          <Link
                            to={link.href}
                            className={`flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium transition-colors ${
                              isActive(link.href)
                                ? 'bg-primary/10 text-primary'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80'
                            }`}
                          >
                            <Icon className="w-5 h-5" />
                            {link.label}
                          </Link>
                        </SheetClose>
                      );
                    })}
                    </nav>
                    {/* Top & bottom fade scroll hints */}
                    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-6 bg-gradient-to-b from-background to-transparent" />
                    <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-background to-transparent" />
                  </div>

                  <div className="border-t border-border/40 mx-4" />

                  {user ? (
                    <div className="flex flex-col gap-1 p-4">
                      <SheetClose asChild>
                        <Link
                          to="/profile"
                          className="flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80"
                        >
                          <User className="w-5 h-5" />
                          Profile
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link
                          to="/my-listings"
                          className="flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80"
                        >
                          <LayoutList className="w-5 h-5" />
                          My Listings
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <Link
                          to="/usage"
                          className="flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted/80"
                        >
                          <BarChart3 className="w-5 h-5" />
                          Usage Dashboard
                        </Link>
                      </SheetClose>
                      <SheetClose asChild>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            handleCreateListing();
                          }}
                          className="flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80 mt-2"
                        >
                          <Sparkles className="w-5 h-5" />
                          Create Listing
                        </button>
                      </SheetClose>
                      <button
                        onClick={() => {
                          handleLogout();
                          setMobileMenuOpen(false);
                        }}
                        className="flex items-center gap-3 px-4 py-3.5 min-h-[48px] rounded-xl text-base font-medium text-destructive hover:bg-destructive/10 active:bg-destructive/15 mt-1"
                      >
                        <LogOut className="w-5 h-5" />
                        Logout
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 p-4">
                      <SheetClose asChild>
                        <Button variant="outline" onClick={handleLogin} className="w-full justify-start min-h-[48px] text-base">
                          <LogIn className="w-5 h-5 mr-2" />
                          Login
                        </Button>
                      </SheetClose>
                      <SheetClose asChild>
                        <Button variant="default" onClick={handleLogin} className="w-full justify-start min-h-[48px] text-base">
                          <Sparkles className="w-5 h-5 mr-2" />
                          Get Started
                        </Button>
                      </SheetClose>
                    </div>
                  )}

                  <div className="mt-auto p-4 border-t border-border/40">
                    <p className="text-xs text-muted-foreground text-center">
                      © {new Date().getFullYear()} ListBright. All rights reserved.
                    </p>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
}
